import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  IconButton,
  AccessLogIcon,
  List,
  ListItem,
  ListSeparator,
  Menu,
  TreeDotsIcon,
  Dialog,
  Header3,
  CancelCrossIcon,
  NotificationBellIcon,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import ReminderFormContainer from "~/components/Reminders/ReminderFormContainer";
import { moveToTrash } from "~/store/actions/actions.discussions";
import { getDiscussionById } from "~/store/selectors/selector.discussions";
import { getProjectById } from "~/store/selectors/selector.projects";
import {
  getLoggedUser,
  hasLoggedUserElevatedPermissions,
  isLoggedUserClientSelector,
  makeIsLoggedUserOwnerOrProjectLeadForProject,
} from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { ConvertToTaskOption } from "./ConvertToTaskOption";
import { DiscussionMoveCopyToProject } from "./DiscussionMoveCopyToProject";
import AccessLogs from "../../AccessLogs/AccessLogs";
import { InsertLinkListItem } from "../../InsertLinkListItem/InsertLinkListItem";
import { TrashOption } from "../../ModalObjects";

export const DiscussionOptions = ({ id, projectId, className, createdById }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reminderModal, setReminderModal] = useState(false);
  const [showAccessLogModal, setShowAccessLogModal] = useState(false);
  const dispatch = useDispatch();

  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const loggedUser = useSelector(getLoggedUser);
  const showOption = !isLoggedUserClient || createdById === loggedUser.get("id");
  const discussion = useSelector((state) => getDiscussionById(state, id));
  const getIsLoggedUserOwnerOrProjectLeaderSelector = useMemo(makeIsLoggedUserOwnerOrProjectLeadForProject, []);
  const canSeeAccessLog = useSelector((state) => getIsLoggedUserOwnerOrProjectLeaderSelector(state, projectId));
  const hasElevetedPermissions = useSelector(hasLoggedUserElevatedPermissions);
  const project = useSelector((state) => getProjectById(state, projectId));
  const can = useSecurity();
  const canAddTask = can(["can_add_task"], project);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleShowAccessLog = useCallback(() => setShowAccessLogModal(true), []);
  const handleCloseAccessLog = useCallback(() => setShowAccessLogModal(false), []);

  const handleMoveToTrash = useCallback(() => {
    dispatch(moveToTrash(id));
  }, [dispatch, id]);

  const showReminders = !isLoggedUserClient;
  const showRemindersForm = !isLoggedUserClient && reminderModal;

  const handleClickReminder = useCallback(() => {
    setReminderModal(true);
    setOpen(false);
  }, []);

  const handleCloseReminder = useCallback(() => {
    setReminderModal(false);
  }, []);

  const projectMembers = project && project.members ? project.members.toArray() : [];

  return (
    <>
      <Menu
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        target={
          <IconButton
            variant="text gray"
            className={className}
            active={open}
          >
            <TreeDotsIcon />
          </IconButton>
        }
        menuClassName="tw-relative tw-overflow-hidden"
      >
        <List className="tw-py-2">
          {showOption && hasElevetedPermissions && (
            <>
              {canAddTask ? (
                <ConvertToTaskOption
                  projectId={projectId}
                  discussionId={id}
                />
              ) : null}
              <DiscussionMoveCopyToProject
                projectId={projectId}
                discussionId={id}
                onSuccess={handleClose}
              />
              <ListSeparator />
            </>
          )}
          <InsertLinkListItem
            link={`${Angie.root_url}/projects/${projectId}/discussions/${id}`}
            onCopy={handleClose}
          />
          {canSeeAccessLog && (
            <ListItem onClick={handleShowAccessLog}>
              <AccessLogIcon className="tw-fill-theme-900" />
              {t("Access Logs")}
            </ListItem>
          )}
          {showReminders ? (
            <ListItem onClick={handleClickReminder}>
              <NotificationBellIcon className="tw-fill-theme-900 tw-shrink-0" />
              {t("Add Reminder")}
            </ListItem>
          ) : null}
          {showOption && (
            <>
              <ListSeparator />
              <TrashOption onMoveToTrash={handleMoveToTrash} />
            </>
          )}
        </List>
      </Menu>

      <Dialog
        open={showAccessLogModal}
        onClose={handleCloseAccessLog}
      >
        <Dialog.Title
          disableDefaultHeading
          className="tw-flex tw-justify-between tw-items-center"
        >
          <Header3>{t("Access Logs")}</Header3>
          <IconButton
            onClick={handleCloseAccessLog}
            variant="text gray"
          >
            <CancelCrossIcon />
          </IconButton>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <AccessLogs
            objectId={id}
            objectType="Discussion"
          />
        </Dialog.Content>
      </Dialog>
      {showRemindersForm ? (
        <ReminderFormContainer
          open={reminderModal}
          parentId={id}
          parent={discussion}
          parentType="Discussion"
          members={projectMembers}
          onClose={handleCloseReminder}
          onCancel={handleCloseReminder}
          onSubmit={handleCloseReminder}
        />
      ) : null}
    </>
  );
};

DiscussionOptions.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  className: PropTypes.string,
  isPinned: PropTypes.bool,
  isHiddenFromClients: PropTypes.bool,
  isLoggedUserClient: PropTypes.bool,
  createdById: PropTypes.number,
  onAddReminder: PropTypes.func,
};
